import { randomString, sha256 } from "./PkceUtils";
import type { OidcConfig } from "./OidcConfig";

interface OidcDiscovery {
	authorization_endpoint: string;
	token_endpoint: string;
	userinfo_endpoint: string;
	end_session_endpoint: string;
}

interface TokenResponse {
	access_token: string;
	expires_in: number;
	refresh_token?: string;
	id_token?: string;
	token_type: string;
}

interface StoredToken {
	accessToken: string;
	refreshToken?: string;
	idToken?: string;
	expiresAt: number;
}

const STORAGE_KEY = "portal.oidc.token";
const PKCE_KEY = "portal.oidc.pkce";
// Survives the logout redirect: an existing SSO session would otherwise answer the next
// authorization request itself and never ask the identity provider (card 31, Review Focus 3).
const IDP_HINT_KEY = "portal.oidc.idpHint";

/**
 * OpenID Connect (Authorization Code + PKCE) client for Keycloak public client `portal-spa`.
 * Tokens persist in sessionStorage (cleared on tab close).
 *
 * @namespace computec.b2b.portal.auth
 */
export default class OidcClient {
	private config: OidcConfig;
	private discovery: OidcDiscovery | null = null;
	private refreshTimer: number | null = null;
	private refreshListeners: Array<(accessToken: string) => void> = [];

	constructor(config: OidcConfig) {
		this.config = config;
	}

	/**
	 * Rejestruje callback wywoływany po **każdym** udanym refresh access tokena (ale NIE po
	 * pierwszym handleCallback — bootstrap startuje z aktualnym tokenem). Component.ts używa
	 * tego, żeby zsynchronizować nagłówki Authorization w ODataModel po wygaśnięciu — bez tego
	 * OData binding zaczyna dawać 401 po pierwszym refresh.
	 */
	public onTokenRefreshed(cb: (accessToken: string) => void): void {
		this.refreshListeners.push(cb);
	}

	private notifyTokenRefreshed(token: string): void {
		for (const cb of this.refreshListeners) {
			try {
				cb(token);
			} catch (err) {
				console.warn("[Portal] OIDC refresh listener threw:", err);
			}
		}
	}

	/**
	 * Top-level entry: handles redirect callback, refreshes if needed, otherwise starts login.
	 * Resolves once a valid access token is available, or never returns (redirects to IdP).
	 */
	public async ensureAuthenticated(): Promise<string> {
		const url = new URL(window.location.href);
		if (url.searchParams.has("code") && url.searchParams.has("state")) {
			try {
				await this.handleCallback(url);
			} catch (err) {
				// Stale callback (state mismatch / expired PKCE / replay) — wyczyść i restartuj.
				// Bez tego SPA crashuje na bootstrapie i user musi ręcznie zrobić Ctrl+Shift+R.
				 
				console.warn("[Portal] OIDC callback failed, restarting login flow:", err);
				sessionStorage.removeItem(STORAGE_KEY);
				sessionStorage.removeItem(PKCE_KEY);
				url.searchParams.delete("code");
				url.searchParams.delete("state");
				url.searchParams.delete("session_state");
				url.searchParams.delete("iss");
				url.searchParams.delete("error");
				url.searchParams.delete("error_description");
				const newSearch = url.searchParams.toString();
				window.history.replaceState({}, "", url.pathname + (newSearch ? "?" + newSearch : "") + url.hash);
				await this.startLogin();
				return new Promise(() => { /* hangs, redirect in progress */ });
			}
			url.searchParams.delete("code");
			url.searchParams.delete("state");
			url.searchParams.delete("session_state");
			url.searchParams.delete("iss");
			const newSearch = url.searchParams.toString();
			window.history.replaceState({}, "", url.pathname + (newSearch ? "?" + newSearch : "") + url.hash);
		}

		const stored = this.getStoredToken();
		if (stored && stored.expiresAt > Date.now() + 30_000) {
			this.scheduleRefresh(stored);
			return stored.accessToken;
		}

		if (stored?.refreshToken) {
			try {
				const refreshed = await this.doRefresh(stored.refreshToken);
				this.scheduleRefresh(refreshed);
				return refreshed.accessToken;
			} catch {
				// fall through to interactive login
			}
		}

		await this.startLogin();
		// startLogin redirects; this never resolves
		return new Promise(() => { /* hangs */ });
	}

	public getAccessToken(): string | null {
		const t = this.getStoredToken();
		return t && t.expiresAt > Date.now() ? t.accessToken : null;
	}

	public getIdTokenClaims(): Record<string, unknown> | null {
		const t = this.getStoredToken();
		return t?.idToken ? this.decodeJwt(t.idToken) : null;
	}

	public getAccessTokenClaims(): Record<string, unknown> | null {
		const t = this.getStoredToken();
		return t ? this.decodeJwt(t.accessToken) : null;
	}

	private decodeJwt(token: string): Record<string, unknown> | null {
		const parts = token.split(".");
		if (parts.length < 2) {
			return null;
		}
		try {
			const padded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
			const padding = "=".repeat((4 - padded.length % 4) % 4);
			const json = atob(padded + padding);
			return JSON.parse(json) as Record<string, unknown>;
		} catch {
			return null;
		}
	}

	public async logout(): Promise<void> {
		const d = await this.getDiscovery();
		const idTokenHint = this.getStoredToken()?.idToken;
		sessionStorage.removeItem(STORAGE_KEY);
		sessionStorage.removeItem(PKCE_KEY);
		if (this.refreshTimer !== null) {
			window.clearTimeout(this.refreshTimer);
			this.refreshTimer = null;
		}
		const u = new URL(d.end_session_endpoint);
		u.searchParams.set("post_logout_redirect_uri", this.config.redirectUri);
		u.searchParams.set("client_id", this.config.clientId);
		if (idTokenHint) {
			u.searchParams.set("id_token_hint", idTokenHint);
		}
		window.location.href = u.toString();
	}

	public get staffIdpHint(): string | null {
		return this.config.staffIdpHint;
	}

	/**
	 * Ends the current Keycloak session and signs back in through the given identity provider.
	 * The hint is kept in sessionStorage across the logout redirect and consumed by the next login.
	 */
	public async loginWithIdentityProvider(idpHint: string): Promise<void> {
		sessionStorage.setItem(IDP_HINT_KEY, idpHint);
		try {
			await this.logout();
		} catch (error) {
			// The logout never redirected: drop the hint so it cannot leak into an unrelated later sign-in.
			sessionStorage.removeItem(IDP_HINT_KEY);
			throw error;
		}
	}

	/**
	 * Force interactive re-login. Czyści cached tokens i przekierowuje do KC.
	 * Wywoływane przez AuthFetch / OData handler gdy API zwraca 401 (token expired,
	 * KC sub mismatch po reimporcie realm, etc.). Promise nigdy nie resolwuje —
	 * następuje window.location.href redirect do IdP.
	 */
	public async forceRelogin(): Promise<never> {
		sessionStorage.removeItem(STORAGE_KEY);
		await this.startLogin();
		return new Promise<never>(() => { /* hangs, redirect in progress */ });
	}

	private async startLogin(): Promise<void> {
		const codeVerifier = randomString(32);
		const codeChallenge = await sha256(codeVerifier);
		const state = randomString(16);
		sessionStorage.setItem(PKCE_KEY, JSON.stringify({
			codeVerifier,
			state,
			returnHash: window.location.hash || ""
		}));

		const d = await this.getDiscovery();
		const u = new URL(d.authorization_endpoint);
		u.searchParams.set("client_id", this.config.clientId);
		u.searchParams.set("redirect_uri", this.config.redirectUri);
		u.searchParams.set("response_type", "code");
		u.searchParams.set("scope", this.config.scopes);
		u.searchParams.set("state", state);
		u.searchParams.set("code_challenge", codeChallenge);
		u.searchParams.set("code_challenge_method", "S256");
		const idpHint = sessionStorage.getItem(IDP_HINT_KEY);
		if (idpHint) {
			sessionStorage.removeItem(IDP_HINT_KEY);
			u.searchParams.set("kc_idp_hint", idpHint);
		}
		window.location.href = u.toString();
	}

	private async handleCallback(url: URL): Promise<void> {
		const code = url.searchParams.get("code");
		const state = url.searchParams.get("state");
		const raw = sessionStorage.getItem(PKCE_KEY);
		if (!raw) {
			throw new Error("OIDC PKCE state missing");
		}
		const stored = JSON.parse(raw) as { codeVerifier: string; state: string; returnHash: string };
		if (stored.state !== state) {
			throw new Error("OIDC state mismatch");
		}

		const d = await this.getDiscovery();
		const body = new URLSearchParams();
		body.set("grant_type", "authorization_code");
		body.set("client_id", this.config.clientId);
		body.set("code", code);
		body.set("redirect_uri", this.config.redirectUri);
		body.set("code_verifier", stored.codeVerifier);

		const r = await fetch(d.token_endpoint, {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: body.toString()
		});
		if (!r.ok) {
			throw new Error("OIDC token exchange failed: " + String(r.status));
		}
		const tokens = (await r.json()) as TokenResponse;
		const stored2 = this.storeToken(tokens);
		this.scheduleRefresh(stored2);
		sessionStorage.removeItem(PKCE_KEY);
		if (stored.returnHash) {
			window.location.hash = stored.returnHash;
		}
	}

	private async doRefresh(refreshToken: string): Promise<StoredToken> {
		const d = await this.getDiscovery();
		const body = new URLSearchParams();
		body.set("grant_type", "refresh_token");
		body.set("client_id", this.config.clientId);
		body.set("refresh_token", refreshToken);
		const r = await fetch(d.token_endpoint, {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: body.toString()
		});
		if (!r.ok) {
			throw new Error("OIDC refresh failed: " + String(r.status));
		}
		const t = (await r.json()) as TokenResponse;
		return this.storeToken(t);
	}

	private scheduleRefresh(stored: StoredToken): void {
		if (!stored.refreshToken) {
			return;
		}
		if (this.refreshTimer !== null) {
			window.clearTimeout(this.refreshTimer);
		}
		const lead = 60_000;
		const delay = Math.max(stored.expiresAt - Date.now() - lead, 5_000);
		this.refreshTimer = window.setTimeout(() => {
			void this.doRefresh(stored.refreshToken)
				.then(refreshed => {
					this.notifyTokenRefreshed(refreshed.accessToken);
					this.scheduleRefresh(refreshed);
				})
				.catch(() => {
					// next request that hits 401 will trigger interactive login
				});
		}, delay);
	}

	private storeToken(t: TokenResponse): StoredToken {
		const stored: StoredToken = {
			accessToken: t.access_token,
			refreshToken: t.refresh_token,
			idToken: t.id_token,
			expiresAt: Date.now() + (t.expires_in * 1000)
		};
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
		return stored;
	}

	private getStoredToken(): StoredToken | null {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		return raw ? (JSON.parse(raw) as StoredToken) : null;
	}

	private async getDiscovery(): Promise<OidcDiscovery> {
		if (this.discovery) {
			return this.discovery;
		}
		const r = await fetch(`${this.config.issuer}/.well-known/openid-configuration`);
		if (!r.ok) {
			throw new Error("OIDC discovery failed");
		}
		this.discovery = await r.json() as OidcDiscovery;
		return this.discovery;
	}
}
